import React, { useState, useRef, useEffect } from 'react';
import './Properties.scss';
import AirbnbIcon from '../../assets/icons/airbnb.svg';
import BookingIcon from '../../assets/icons/booking-com.svg';
import FPIcon from '../../assets/icons/fp-booking-site.svg';
import ViewIcon from '../../assets/icons/view.png';
import CopyIcon from '../../assets/icons/copy.png';

const PropertyItem = ({ property, t, onPropertyClick }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const menuRef = useRef(null);
  const tooltipTimer = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    };
  }, []);

  const handleIconClick = (e, iconType) => {
    e.stopPropagation();
    setCopiedUrl(null);
    setActiveMenu(activeMenu === iconType ? null : iconType);
  };

  const handleView = (e, url) => {
    e.stopPropagation();
    setActiveMenu(null);

    if (window.cordova && window.cordova.InAppBrowser) {
      // Use InAppBrowser to open in system browser (Safari)
      window.cordova.InAppBrowser.open(url, '_system');
    } else {
      // Fallback for web
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopy = async (e, url, iconType) => {
    e.stopPropagation();
    setActiveMenu(null);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(iconType);
      tooltipTimer.current = setTimeout(() => {
        setCopiedUrl(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedUrl(iconType);
        tooltipTimer.current = setTimeout(() => {
          setCopiedUrl(null);
        }, 2000);
      } catch (err2) {
        console.error('Fallback copy failed:', err2);
      }
      document.body.removeChild(textArea);
    }
  };

  const { picture, address, address_2: address2, is_active: active, bedrooms, nickname } = property;
  const thumbnail = picture?.startsWith('http')
    ? picture
    : picture?.startsWith('//')
    ? `https:${picture}`
    : `https//:${picture}`;

  const renderPlatformIcon = (iconSrc, alt, url, iconType) => (
    <div
      className="platform-icon-wrapper"
      key={iconType}
      ref={activeMenu === iconType ? menuRef : null}
    >
      <img
        src={iconSrc}
        alt={alt}
        className={`platform-icon ${activeMenu === iconType ? 'active' : ''}`}
        onClick={(e) => handleIconClick(e, iconType)}
      />
      {activeMenu === iconType && (
        <div className="platform-actions-menu">
          <button
            type="button"
            className="action-btn view-btn"
            onClick={(e) => handleView(e, url)}
            title="View Link"
          >
            <img src={ViewIcon} alt="View link" className="action-icon" />
          </button>
          <button
            type="button"
            className="action-btn copy-btn"
            onClick={(e) => handleCopy(e, url, iconType)}
            title="Copy Link"
          >
            <img src={CopyIcon} alt="Copy link" className="action-icon" />
          </button>
        </div>
      )}
      {copiedUrl === iconType && activeMenu !== iconType && (
        <div className="copied-message">Copied!</div>
      )}
    </div>
  );

  return (
    <div className="property" onClick={() => onPropertyClick(property)} role="button" tabIndex="0">
      <div className="property-picture">
        <div
          className="picture"
          style={{
            backgroundImage: `url('${thumbnail}')`,
          }}
        />
      </div>
      <div className="property-info">
        <p className="property-nickname property-single-line">
          <i className="icon icon-home" />
          {nickname}
        </p>
        <p className="property-location property-single-line">{address2 || address}</p>
        <div className="round-separator">
          <div className="circle" />
        </div>
        <div className="property-last">
          <div className="property-meta-left">
            <span className="property-bedrooms">
              {bedrooms > -1 ? (
                <>
                  {bedrooms > 1
                    ? `${bedrooms} ${t('bedrooms')}`
                    : bedrooms > 0
                    ? `${bedrooms} ${t('bedroom')}`
                    : t('studio')}
                </>
              ) : null}
            </span>
            {property.property_links !== null && (
              <div className="property-platforms">
                {property.property_links.airbnb !== null &&
                  renderPlatformIcon(
                    AirbnbIcon,
                    'Airbnb',
                    property.property_links.airbnb,
                    'airbnb'
                  )}
                {property.property_links.bookingCom !== null &&
                  renderPlatformIcon(
                    BookingIcon,
                    'Booking.com',
                    property.property_links.bookingCom,
                    'booking'
                  )}
                {property.property_links.general !== null &&
                  renderPlatformIcon(FPIcon, 'FP Booking', property.property_links.general, 'fp')}
              </div>
            )}
          </div>
          <div className="property-meta-right">
            <div className={`property-activity${active ? ' property-active' : ''}`}>
              <i className="icon icon-tick" />
              <span className="property-activity-text">{active ? t('active') : t('inactive')}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="column-arrow">
        <i className="icon icon-arrow" />
      </div>
    </div>
  );
};

export default PropertyItem;
